import Button from 'ext-ui/components/Button/Button'
import React, { useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import { selectFilteredPeopleMembers } from 'src/app/PeopleMembers/redux/peopleMembers.selectors'
import UserDropdown, {
  UserDropdownInfiniteScrollProps,
  userIdOption,
  UserOption,
} from 'src/components/UserDropdown/UserDropdown'
import { OrganizationRoles } from 'src/enums'
import { FormFieldsValidation, useFormFields } from 'src/hooks'
import { useTranslate } from 'src/localization/Trns'
import { UserListModel } from 'src/models/user.models'
import { useQueryAddDepartmentMember } from 'src/queries/organizations/department.queries'
import { useQueryOrgMembers } from 'src/queries/organizations/members.queries'
import { stringArrayIsEmpty } from 'src/utils/validation/string.validation'
import { css, FlattenSimpleInterpolation } from 'styled-components'

interface AddDeptMembersFormProps {
  organizationId: string
  departmentId: string
  departmentName: string
  setEditing: (editing: boolean) => void
  styles?: FlattenSimpleInterpolation
}

export type AddDepartmentMembersFields = {
  userIds: string[]
}

const AddDeptMembersForm: React.FC<AddDeptMembersFormProps> = (props) => {
  const { mutate: postAddDeptMembers, isPending: postAddDeptMembersLoading } =
    useQueryAddDepartmentMember(
      props.organizationId || '',
      props.departmentId || '',
      props.departmentName || '',
      (): void => props.setEditing(false),
    )
  const memberListModels = useSelector(selectFilteredPeopleMembers)
  const [search, setSearch] = useState<string>()

  const {
    data: orgMembersData,
    fetchNextPage,
    hasNextPage,
    isFetching,
  } = useQueryOrgMembers({
    orgId: props.organizationId,
    queryParams: {
      search,
      isInDepartment: false,
      organizationRoles: [
        OrganizationRoles.owner,
        OrganizationRoles.admin,
        OrganizationRoles.bookkeeper,
        OrganizationRoles.cardManager,
        OrganizationRoles.member,
      ],
    },
  })

  const { analytics } = useAnalyticsContext()
  const translate = useTranslate()

  const { fields: flatFields, onFieldChange: onFlatFieldChange } =
    useFormFields<AddDepartmentMembersFields>(
      {
        userIds: [],
      },
      (): FormFieldsValidation<AddDepartmentMembersFields> => {
        return {
          userIds: [stringArrayIsEmpty],
        }
      },
    )

  const fetchUsers = (filterInput?: string): void => {
    if (filterInput !== search) setSearch(filterInput)
    else fetchNextPage()
  }

  const infiniteScrollProps: UserDropdownInfiniteScrollProps = {
    fetch: fetchUsers,
    page: orgMembersData?.page ?? 0,
    complete: !hasNextPage && !isFetching,
    loading: isFetching,
  }

  const userIdSet =
    flatFields.userIds !== undefined
      ? new Set(flatFields.userIds)
      : new Set<string>()

  const userIdMap = useMemo(() => {
    return Object.values(memberListModels).reduce(
      (acc: Record<string, UserListModel>, curr) => ({
        ...acc,
        [curr.id]: curr,
      }),
      {},
    )
  }, [memberListModels])

  const options =
    orgMembersData?.members?.map((member) => userIdOption(member)) ?? []

  const selectedOptions: UserOption[] | undefined = useMemo(
    () =>
      flatFields.userIds !== undefined
        ? flatFields.userIds
            .filter((val) => userIdMap[val] !== undefined)
            .map((val) => userIdOption(userIdMap[val]))
        : undefined,
    [flatFields.userIds],
  )

  const onUserIdChange = (val: string[]): void => {
    onFlatFieldChange('userIds', val)
  }

  const addDeleteVal = (newSet: Set<string>, val: string): void => {
    if (newSet.has(val)) {
      newSet.delete(val)
    } else {
      newSet.add(val)
    }
  }

  const handleUserSelection = (val: string): void => {
    const newSet = new Set(flatFields.userIds)
    addDeleteVal(newSet, val)
    onUserIdChange(Array.from(newSet))
  }

  const handleMultiUserSelection = (val: string[]): void => {
    const newSet = new Set(flatFields.userIds)
    val.map((val) => {
      addDeleteVal(newSet, val)
    })
    onUserIdChange(Array.from(newSet))
  }

  const onSuccessfulSubmit = (): void => {
    postAddDeptMembers({ body: { userIds: flatFields.userIds } })
    props.setEditing(false)
    analytics.track('add department member - confirm', {
      number_members: flatFields.userIds.length,
    })
  }
  return (
    <form css={props.styles}>
      <div
        css={css`
          display: flex;
          align-items: center;
          gap: 24px;
          align-self: stretch;
          flex-direction: row;
          width: 100%;
        `}
      >
        <div
          css={css`
            width: 100%;
          `}
        >
          <UserDropdown
            optionGroups={[
              {
                label: translate('Members'),
                options: options,
              },
            ]}
            selectedOptions={selectedOptions}
            isCreatable={false}
            chooseOption={handleUserSelection}
            chooseMultiOption={handleMultiUserSelection}
            selectedValues={userIdSet}
            infiniteScrollProps={infiniteScrollProps}
            isMulti
            displayPills
            placeholder={translate('Add Members to Team')}
          />
        </div>
        <div
          css={css`
            display: flex;
            align-items: center;
            gap: 12px;
          `}
        >
          <Button
            variant={'primary'}
            dropShadow={false}
            onClick={onSuccessfulSubmit}
            loading={postAddDeptMembersLoading}
          >
            {translate('Save')}
          </Button>
          <Button
            variant={'secondary'}
            dropShadow={false}
            onClick={(): void => {
              props.setEditing(false)
              analytics.track('add department member - cancel')
            }}
          >
            {translate('Cancel')}
          </Button>
        </div>
      </div>
    </form>
  )
}

export default AddDeptMembersForm
